'use client';

import { trpc } from "@plura/shared/trpc";
import { LeadForm } from "@plura/shared/types";
import { motion } from "framer-motion";
import { useFormContext } from 'react-hook-form';
import "./conversation-exit-screen.css";
import { dynamicQuestionContent, getCurrentResponseScore } from "@plura/shared/lead-forms";
import { Loader } from "@plura/client/components";

interface Props {
  id: string;
  leadForm: LeadForm;
  title: string | null;
  caption: string | null;
  exitCallToAction: string | null;
  exitCallToActionLink: string | null;
  editorMode: boolean;
  previewMode: boolean;
  resetForm: () => void;
  onContentClick?: (fieldName: string, blockId: string) => void;
  hiddenFields?: {
    field: string;
    value: string;
  }[];
}

const ConversationExitScreen = ({ id, leadForm, title, caption, exitCallToAction, exitCallToActionLink, editorMode, previewMode, resetForm, onContentClick, hiddenFields }: Props) => {
  const form = useFormContext()
  const { data: exitMessage, isLoading: exitMessageLoading } = trpc.leadForm.generateExitMessage.useQuery({
    about: leadForm.about ?? '',
    purpose: leadForm.overview ?? '',
    tone: leadForm.tone ?? '',
    snippets: leadForm.snippets?.map(s => s.content) ?? [],
  }, {
    enabled: !title,
    staleTime: Infinity,
  })
  const _caption = caption?.length ? caption : exitMessage
  const isLoading = title ? false : !_caption
  const { conversationId, ...userResponses } = form.getValues()
  const currentResponseScore = userResponses
    ? getCurrentResponseScore(userResponses, leadForm)
    : 0

  const handleContentClick = (field: 'content' | 'secondaryContent') => {
    if (!editorMode || !onContentClick) return
    onContentClick(field, id)
  }

  return (
    <div className="stepHeaderWrapper">
      {
        isLoading
          ? (
            <Loader />
          )
          : (
            <>
              {
                title &&
                <motion.div
                  key={`${id}-title`}
                  exit={{ opacity: 0, transform: 'translateY(-12px)' }}
                  initial={{ opacity: 0, transform: 'translateY(12px)' }}
                  animate={{ opacity: !isLoading ? 1 : 0, transform: !isLoading ? 'translateY(0)' : 'translateY(12px)' }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: 0.05 }}
                  onClick={() => handleContentClick('content')}
                >
                  <h1 dangerouslySetInnerHTML={{
                    __html: dynamicQuestionContent({
                      content: title,
                      score: currentResponseScore,
                      editMode: editorMode,
                      previewMode,
                      hiddenFields,
                      journeyItems: leadForm.journeyItems ?? [],
                      userResponses
                    })
                  }} />
                </motion.div>
              }
              <motion.div
                key={`${id}-caption`}
                exit={{ opacity: 0, transform: 'translateY(-12px)' }}
                initial={{ opacity: 0, transform: 'translateY(12px)' }}
                animate={{ opacity: !isLoading ? 1 : 0, transform: !isLoading ? 'translateY(0)' : 'translateY(12px)' }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
                onClick={() => handleContentClick('secondaryContent')}
              >
                {
                  _caption &&
                  <h4 dangerouslySetInnerHTML={{
                    __html: dynamicQuestionContent({
                      content: _caption,
                      score: currentResponseScore,
                      editMode: editorMode,
                      previewMode,
                      hiddenFields,
                      journeyItems: leadForm.journeyItems ?? [],
                      userResponses
                    })
                  }} />
                }
              </motion.div>

              <motion.div
                key={`${id}-children`}
                className="ctaWrapper"
                exit={{ opacity: 0, transform: 'translateY(-12px)' }}
                initial={{ opacity: 0, transform: 'translateY(12px)' }}
                animate={{ opacity: !isLoading ? 1 : 0, transform: !isLoading ? 'translateY(0)' : 'translateY(12px)' }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: 0.28 }}
              >
                {
                  !!exitCallToAction?.length && !!exitCallToActionLink?.length && (
                    <a className="c-button" href={exitCallToActionLink} target="_blank">
                      <span>{exitCallToAction}</span>
                    </a>
                  )
                }
              </motion.div>
            </>
          )
      }
    </div>
  )
}

export default ConversationExitScreen